import {
    TbCar,
    TbCarOff,
    TbEdit,
    TbHeartHandshake,
    TbHospital,
    TbStatusChange,
    TbUserMinus,
    TbUserPlus,
} from "react-icons/tb";

import DriverAvatar from "@/components/DriverAvatar";
import { PassengerColumnCell } from "@/components/table/PassengerColumn";
import { DashboardPassengerBasicInfo } from "@/schemas/dashboard.schemas";
import {
    IPassengerHistory,
    IPassengerHistoryActor,
    PassengerHistoryActorType,
    PassengerHistoryTypes,
} from "@/schemas/passenger-history.schemas";
import { CommonStatusItem } from "@/types/global";

import { softColors } from "./colors";

export type HistoryTypeRenderProps = {
    item: IPassengerHistory;
    info: HistoryTypeItem;
    passenger?: DashboardPassengerBasicInfo;
};
export type HistoryTypeItem = CommonStatusItem & {
    label: string;
    value: PassengerHistoryTypes;
    render: React.ComponentType<HistoryTypeRenderProps>;
    useChanges: boolean;
    systemActor?: string;
};

export type HistoryTypeList = HistoryTypeItem[];

export function HistoryPassengerItem({ passenger }: { passenger?: DashboardPassengerBasicInfo }) {
    if (!passenger) return null;

    return (
        <>
            <PassengerColumnCell row={passenger} value={passenger.name} raw={false} withoutClipBoard={true} />
            isimli yolcu,
        </>
    );
}

export function CommonHistoryItem({ item: { actor, actorType }, info, passenger }: HistoryTypeRenderProps) {
    if (actorType === PassengerHistoryActorType.USER && !actor) return null;

    return (
        <div className="flex items-center gap-1 font-normal flex-wrap  break-all text-gray-500 text-sm">
            <HistoryPassengerItem passenger={passenger} />
            <div className="flex ">
                {actor ? (
                    <DriverAvatar
                        avatar
                        userType={actor.userType}
                        isDeleted={actor.isDeleted || false}
                        name={actor.name}
                        withoutClipBoard
                    />
                ) : (
                    info.systemActor && (
                        <span className="font-semibold text-sm text-gray-900 !capitalize break-all w-full ">
                            {info.systemActor}
                        </span>
                    )
                )}
            </div>
            <div className=" shrink-0 grow-0"> {info.label} </div>
        </div>
    );
}

export function DriverHistoryItem({
    driver,
    actor,
    label1,
    label2,
    systemActor,
    passenger,
}: {
    label1: string;
    label2: string;
    driver: IPassengerHistoryActor;
    actor: IPassengerHistoryActor;
    systemActor?: string;
    passenger?: DashboardPassengerBasicInfo;
}) {
    return (
        <div className="flex items-center gap-1 font-normal flex-wrap  break-all text-gray-500 text-sm">
            <HistoryPassengerItem passenger={passenger} />
            <div className="flex ">
                {actor ? (
                    <DriverAvatar
                        avatar
                        userType={actor.userType}
                        isDeleted={actor.isDeleted || false}
                        name={actor.name}
                        withoutClipBoard
                    />
                ) : (
                    systemActor && (
                        <span className="font-semibold text-sm text-gray-900 !capitalize break-all w-full ">
                            {systemActor}
                        </span>
                    )
                )}
            </div>
            {label1}
            <div className="flex ">
                <DriverAvatar
                    avatar
                    userType={driver!.userType}
                    isDeleted={driver!.isDeleted || false}
                    name={driver!.name}
                    withoutClipBoard
                />
            </div>
            {label2}
        </div>
    );
}
export function DriverAssignationHistoryItem({ item: { actor, changes }, passenger }: HistoryTypeRenderProps) {
    const driver =
        changes && changes[0] && changes[0].field === "driverId" && changes[0].toDriver && changes[0].toDriver;
    if (!driver) return null;

    return (
        <DriverHistoryItem
            passenger={passenger}
            driver={driver}
            actor={actor}
            label1="tarafından"
            label2="isimli şoföre atandı."
        />
    );
}

export function DriverUnAssignationHistoryItem({
    item: { actor, changes, actorType },
    info,
    passenger,
}: HistoryTypeRenderProps) {
    if (actorType === PassengerHistoryActorType.USER && !actor) return null;
    const driver =
        changes && changes[0] && changes[0].field === "driverId" && changes[0].fromDriver && changes[0].fromDriver;
    if (!driver) return null;

    return (
        <DriverHistoryItem
            passenger={passenger}
            systemActor={info.systemActor}
            driver={driver}
            actor={actor}
            label1="tarafından"
            label2="isimli şoför ataması kaldırıldı."
        />
    );
}

export function WelcomeTeamUpdateHistoryItem({
    item: { actor, changes },
}: {
    item: IPassengerHistory;
    info: HistoryTypeItem;
}) {
    if (!actor || !changes || !changes[0]) return null;
}

export const historyTypes: HistoryTypeList = [
    {
        label: "tarafından  manuel yolcu oluşturuldu.",
        value: PassengerHistoryTypes.CREATE_CUSTOM_PASSENGER,
        component: () => TbUserPlus,
        ...softColors.green,
        tooltip: {
            title: "Yolcu Oluşturma",
            description: "Manuel yolcu oluşturuldu.",
        },
        render: CommonHistoryItem,
        useChanges: false,
    },
    {
        value: PassengerHistoryTypes.WELCOME_TEAM_UPDATE,
        label: "tarafından karşılama ekibi sayfasında yolcu güncellendi.",
        tooltip: {
            title: "Yolcu Güncelleme",
            description: "Yolcu bilgileri güncellendi.",
        },
        ...softColors.deepPurple,
        component: () => TbHeartHandshake,
        render: CommonHistoryItem,
        useChanges: true,
    },
    {
        value: PassengerHistoryTypes.PLAN_PAGE_UPDATE,
        label: "tarafından plan sayfasında yolcu güncellendi.",
        tooltip: {
            title: "Yolcu Güncelleme",
            description: "Yolcu bilgileri plan sayfasında güncellendi.",
        },
        ...softColors.purple,
        component: () => TbEdit,
        render: CommonHistoryItem,
        useChanges: true,
    },
    {
        value: PassengerHistoryTypes.TRACKING_PAGE_UPDATE,
        label: "tarafından takip sayfasında yolcu güncellendi.",
        tooltip: {
            title: "Yolcu Güncelleme",
            description: "Yolcu bilgileri takip sayfasında güncellendi.",
        },
        ...softColors.purple,
        component: () => TbEdit,
        render: CommonHistoryItem,
        useChanges: true,
    },
    {
        value: PassengerHistoryTypes.UHM_UPDATE,
        label: "tarafından UHM projesinde yolcu güncellendi.",
        tooltip: {
            title: "UHM Yolcu Güncelleme",
            description: "Yolcu bilgileri UHM ile güncellendi.",
        },
        ...softColors.indigo,
        component: () => TbHospital,
        render: CommonHistoryItem,
        useChanges: true,
    },
    {
        value: PassengerHistoryTypes.MOBILE_APP_TASK_PROGRESS_UPDATE,
        label: "tarafından mobil uygulama üzerinden yolcu durumu güncellendi.",
        tooltip: {
            title: "Mobil Uygulama Yolcu Durumu Güncelleme",
            description: "Yolcu durumu mobil uygulama üzerinden güncellendi.",
        },
        ...softColors.green,
        component: () => TbStatusChange,
        render: CommonHistoryItem,
        useChanges: true,
    },
    {
        value: PassengerHistoryTypes.PASSENGER_REQUEST_UPDATE,
        label: "tarafından yolcu isteği kabul edilerek yolcu güncellendi.",
        tooltip: {
            title: "Yolca Talebi Güncelleme",
            description: "Yolca talebi güncellendi.",
        },
        ...softColors.green,
        component: () => TbStatusChange,
        render: CommonHistoryItem,
        useChanges: true,
    },
    {
        value: PassengerHistoryTypes.OPERATION_SERVICE_UPDATE,
        label: "tarafından yolcu güncellendi.",
        tooltip: {
            title: "Almanya Yolcu Güncelleme",
            description: "Yolcu bilgileri Almanyadan güncellendi.",
        },
        ...softColors.green,
        component: () => TbEdit,
        render: CommonHistoryItem,
        useChanges: true,
        systemActor: "Almanya",
    },
    {
        value: PassengerHistoryTypes.DRIVER_PLAN_PAGE_ASSIGN,
        label: "şoföre atandı",
        tooltip: {
            title: "Şoför Atama",
            description: "Yolcu transferi için şoför  atandı.",
        },
        ...softColors.blue,
        component: () => TbCar,
        render: DriverAssignationHistoryItem,
        useChanges: false,
    },
    {
        value: PassengerHistoryTypes.DRIVER_PLAN_PAGE_UNASSIGN,
        label: "şoför ataması kaldırıldı",
        tooltip: {
            title: "Şoför Atama Kaldırma",
            description: "Yolcu transferi için şoför ataması kaldırıldı.",
        },
        ...softColors.brown,
        render: DriverUnAssignationHistoryItem,
        component: () => TbCarOff,
        useChanges: false,
    },
    {
        value: PassengerHistoryTypes.OPERATION_SERVICE_UNASSIGN,
        label: "tarafından yolcu güncellendi.",
        tooltip: {
            title: "Almanya Yolcu Güncelleme",
            description: "Yolcu bilgileri Almanyadan güncellendi.",
        },
        ...softColors.brown,
        component: () => TbCarOff,
        render: DriverUnAssignationHistoryItem,
        systemActor: "Almanya",
        useChanges: false,
    },
    {
        value: PassengerHistoryTypes.TRACKING_PAGE_ASSIGN,
        label: "şoföre atandı (görev taşındı)",
        tooltip: {
            title: "Şoför Atama",
            description: "Görev taşıma ile yolcu transferi için şoför atandı.",
        },
        ...softColors.blue,
        component: () => TbCar,
        render: DriverAssignationHistoryItem,
        useChanges: false,
    },
    {
        value: PassengerHistoryTypes.TRACKING_PAGE_UNASSIGN,
        label: "şoför ataması kaldırıldı (görev taşındı)",
        tooltip: {
            title: "Şoför Atama Kaldırma",
            description: "Görev taşıma ile yolcu transferi için şoför ataması kaldırıldı.",
        },
        ...softColors.brown,
        render: DriverUnAssignationHistoryItem,
        component: () => TbCarOff,
        useChanges: false,
    },
    {
        label: "tarafından manuel yolcu silindi.",
        value: PassengerHistoryTypes.DELETE_CUSTOM_PASSENGER,
        component: () => TbUserMinus,
        ...softColors.red,
        tooltip: {
            title: "Yolcu Silme",
            description: "Manuel yolcu silindi.",
        },
        render: CommonHistoryItem,
        useChanges: false,
    },
];
